import { useCallback, useEffect, useRef, useState } from "react"
import { useAudioLevelObserver, useLocalSessionId } from "@daily-co/daily-react"

/** Bar heights (px, of a 16 px meter) at silence; each bar rises with the local microphone level. */
const REST_HEIGHTS = [4, 6, 4]
const PEAK_HEIGHTS = [10, 16, 10]

/**
 * Google Meet's small waveform beside the microphone button: three bars that move with the
 * local microphone level, so you can tell your mic is picking you up without watching a tile.
 * The bars stay flat when the microphone is off.
 */
export function MicLevelMeter({ active }: { active: boolean }) {
  const localSessionId = useLocalSessionId()
  const [level, setLevel] = useState(0)
  const frameRef = useRef<number | null>(null)
  const targetRef = useRef(0)
  const onLevel = useCallback((volume: number) => {
    // Daily reports 0..1; a little gain makes normal speech visibly move the bars.
    targetRef.current = Math.min(1, volume * 2.5)
    if (frameRef.current === null) {
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null
        setLevel(targetRef.current)
      })
    }
  }, [])
  useAudioLevelObserver(localSessionId, onLevel)
  useEffect(() => () => {
    if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
  }, [])
  const shown = active ? level : 0
  return (
    <span data-testid="call-mic-level" data-level={shown.toFixed(2)} aria-hidden="true" className="flex h-4 items-center gap-[2px]">
      {REST_HEIGHTS.map((rest, index) => (
        <span
          key={index}
          className="w-[3px] rounded-full bg-current transition-[height] duration-100"
          style={{ height: `${rest + (PEAK_HEIGHTS[index] - rest) * shown}px` }}
        />
      ))}
    </span>
  )
}
